import { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  InputAdornment,
  Alert,
  CircularProgress,
  useTheme,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { UserContext, ThemeContext } from './Context';
import CguContent from './tool/CguContent';

function UserLogIn({ onCguGateChange }) {
  const { setUser } = useContext(UserContext);
  const { theme: themeMode } = useContext(ThemeContext);
  const theme = useTheme();
  const [matricule, setMatricule] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCguGate, setShowCguGate] = useState(false);
  const [cguEntries, setCguEntries] = useState([]);
  const [cguLanguage, setCguLanguage] = useState('fr');
  const [guardiaOn, setGuardiaOn] = useState(false);
  const [cguChecked, setCguChecked] = useState(false);
  const [cguLoading, setCguLoading] = useState(false);
  const [cguSubmitting, setCguSubmitting] = useState(false);
  const [cguError, setCguError] = useState('');
  const [pendingMatricule, setPendingMatricule] = useState('');

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (typeof onCguGateChange === 'function') {
      onCguGateChange(showCguGate);
    }
  }, [showCguGate, onCguGateChange]);

  const getCguContentByVersionAndLang = (version, lang) => {
    const entry = cguEntries.find((item) => item.version === version && item.lang === lang);
    return entry?.content || '';
  };

  const loadCguContent = async () => {
    setCguLoading(true);
    setCguError('');
    try {
      const response = await fetch('/api/cgu');
      if (!response.ok) {
        throw new Error('Impossible de charger les CGU');
      }
      const data = await response.json();
      setCguEntries(Array.isArray(data) ? data : []);
    } catch (loadError) {
      setCguError('Erreur lors du chargement des CGU.');
    } finally {
      setCguLoading(false);
    }
  };

  const handleAcceptCgu = async () => {
    if (!cguChecked || !pendingMatricule) {
      return;
    }

    setCguSubmitting(true);
    setCguError('');
    try {
      const token = sessionStorage.getItem('authToken');
      await axios.post(
        '/api/user/cgu/accept',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      sessionStorage.setItem('postCguLanding', 'tutorial');
      setShowCguGate(false);
      setUser(pendingMatricule);
    } catch (acceptError) {
      setCguError(
        acceptError.response?.data?.message || 'Erreur lors de la validation des CGU.'
      );
    } finally {
      setCguSubmitting(false);
    }
  };

  const handleCancelCgu = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authExpiration');
    setShowCguGate(false);
    setPendingMatricule('');
    setCguChecked(false);
    setCguError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/user/login', { matricule, password });
      
      const token = response.data.token;
      const expiration = Date.now() + 12 * 60 * 60 * 1000; // 12 heures
      sessionStorage.setItem('authToken', token);
      sessionStorage.setItem('authExpiration', expiration);
      
      const decoded = jwtDecode(token);
      const cguStatus = response.data?.cgu;
      if (cguStatus && !cguStatus.acceptedForCurrentConfig) {
        setPendingMatricule(decoded.matricule);
        setGuardiaOn(Boolean(cguStatus?.currentConfig?.guardiaOn));
        setCguLanguage(response.data?.language === 'nl' ? 'nl' : 'fr');
        setCguChecked(false);
        setShowCguGate(true);
        await loadCguContent();
        return;
      }

      setUser(decoded.matricule);
    } catch (err) {
      setError(
        `${err.response?.data?.message || 'Erreur lors de la connexion. Veuillez vérifier vos identifiants ou contacter votre administrateur. / Fout bij het inloggen. Controleer uw gegevens of neem contact op met uw beheerder.'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const cguTitle = cguLanguage === 'fr'
    ? "Conditions d'utilisation et mentions légales"
    : 'Gebruiksvoorwaarden en wettelijke vermeldingen';
  const blueNoteContent = getCguContentByVersionAndLang('bluenote', cguLanguage);
  const guardiaContent = guardiaOn ? getCguContentByVersionAndLang('guardia', cguLanguage) : '';
  const consentText = cguLanguage === 'fr'
    ? "Je reconnais avoir pris connaissance et accepter sans réserve les Conditions Générales d’Utilisation de BlueNote."
    : 'Ik verklaar dat ik kennis heb genomen van en zonder voorbehoud akkoord ga met de Algemene Gebruiksvoorwaarden van BlueNote.';

  if (showCguGate) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            {cguTitle}
          </Typography>
          <Button variant="contained" onClick={() => setCguLanguage(cguLanguage === 'fr' ? 'nl' : 'fr')}>
            {cguLanguage === 'fr' ? 'Nederlands' : 'Français'}
          </Button>
        </Box>

        <Divider />

        {cguError && <Alert severity="error">{cguError}</Alert>}

        {cguLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: '42vh', overflow: 'auto', pr: 1 }}>
            {blueNoteContent ? (
              <CguContent content={blueNoteContent} />
            ) : (
              <Typography variant="body2">
                {cguLanguage === 'fr' ? 'Aucun contenu disponible.' : 'Geen inhoud beschikbaar.'}
              </Typography>
            )}
            {guardiaOn && guardiaContent && (
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 700,
                    letterSpacing: 1,
                    color: 'text.secondary',
                  }}
                >
                  {cguLanguage === 'fr'
                    ? 'Module additionnel GuardIA'
                    : 'Aanvullende module GuardIA'}
                </Typography>
                <CguContent content={guardiaContent} />
              </Box>
            )}
          </Box>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={cguChecked}
              onChange={(e) => setCguChecked(e.target.checked)}
            />
          }
          label={<Typography variant="body2">{consentText}</Typography>}
        />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={handleCancelCgu}
            disabled={cguSubmitting}
            sx={{ flex: 1 }}
          >
            {cguLanguage === 'fr' ? 'Annuler' : 'Annuleren'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            disabled={!cguChecked || cguSubmitting || cguLoading}
            onClick={handleAcceptCgu}
            sx={{ flex: 1 }}
          >
            {cguSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : (cguLanguage === 'fr' ? 'Continuer' : 'Verdergaan')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
        '& .MuiTextField-root': {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main,
            },
          },
        },
      }}
    >
      <TextField
        label="Numéro d'identification / Identificatienummer"
        variant="outlined"
        fullWidth
        value={matricule}
        onChange={(e) => setMatricule(e.target.value)}
        inputProps={{
          minLength: 9,
          maxLength: 9
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon color="primary" />
            </InputAdornment>
          ),
        }}
        required
        sx={{
          '& label': {
            color: theme.palette.text.secondary,
          },
          '& label.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }}
      />
      
      <TextField
        label="Mot de passe / Wachtwoord"
        type={showPassword ? "text" : "password"}
        variant="outlined"
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="Afficher/masquer le mot de passe / Wachtwoord tonen/verbergen"
                onClick={handleTogglePasswordVisibility}
                edge="end"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        required
        sx={{
          '& label': {
            color: theme.palette.text.secondary,
          },
          '& label.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }}
      />
      
      {error && (
        <Alert 
          severity="error"
          sx={{
            backgroundColor: themeMode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : undefined,
            color: themeMode === 'dark' ? '#ff7070' : undefined,
          }}
        >
          {error}
        </Alert>
      )}
      
      <Button 
        type="submit" 
        variant="contained" 
        color="primary" 
        fullWidth 
        size="large"
        disabled={loading}
        sx={{ 
          mt: 1,
          py: 1.5,
          borderRadius: 2,
          fontWeight: 'bold',
          backgroundColor: theme.palette.primary.main,
          color: themeMode === 'dark' ? theme.palette.primary.contrastText : '#fff',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          }
        }}
      >
        {loading ? (
          <CircularProgress 
            size={24} 
            sx={{ 
              color: themeMode === 'dark' ? theme.palette.primary.main : '#fff'
            }} 
          />
        ) : (
          "Se connecter / Aanmelden"
        )}
      </Button>
    </Box>
  );
}

export default UserLogIn;